import { closestCenter, pointerWithin, rectIntersection } from '@dnd-kit/core';
import { parseDndId } from './dndId';

// A container in this feature is simultaneously a disabled sortable slot
// in the top-level list AND the owner of its own nested children
// SortableContext - dnd-kit's well-documented "multiple containers" case,
// where naive/default collision detection can resolve `over` to whatever's
// geometrically closest across the WHOLE DndContext rather than what's
// actually under the pointer. Standard recipe (this is genuinely new
// territory for this codebase - zero collisionDetection usage exists
// anywhere else, including Pipeline's own dnd-kit setup, which gets away
// with the default since kanban columns never nest): try pointerWithin
// first (accurate, but returns nothing in the gaps BETWEEN rects), fall
// back to rectIntersection (overlap-based, still returns nothing if the
// dragged item's own small overlay rect doesn't actually overlap anything -
// a real, observed case: the tiny DragOverlay hovering the dead space
// between a tall container's bottom edge and the next dropzone below it,
// touching neither), then closestCenter as a final geometric tier (pure
// nearest-distance, so it ALWAYS resolves to something even with zero
// overlap).
//
// A container's own outer card rect always geometrically CONTAINS its
// children's rects too (and, for the top-level list, can also sit right
// next to purpose-built dropzones like topLevelEnd/containerEmpty), so more
// than one candidate can legitimately match at once within a single tier -
// none of the three strategies prioritize by nesting depth or specificity
// on their own. Whenever a tier's results include a more specific
// (non-container) candidate, prefer it over the bare container-slot one.
//
// Critically, a tier "succeeding" with a container-only match must NOT
// short-circuit the cascade - a real bug shipped from exactly that: right
// at the boundary between a container and topLevelEnd, rectIntersection
// would find ONLY the container (a few px of genuine overlap) while
// topLevelEnd sat just out of reach - a non-empty, but low-quality, result
// that stopped the cascade before closestCenter (which WOULD have found
// topLevelEnd - nearest-distance never comes up empty) ever ran. So: keep
// advancing through tiers as long as all we have is a container-only
// match, remembering the first one as a fallback, and only fall through to
// that fallback (or the last known-good id) if NO tier ever produces a
// genuinely specific candidate.
function classify(collisions) {
   const specific = collisions.filter((collision) => parseDndId(String(collision.id)).kind !== 'container');
   return specific.length > 0 ? specific : null;
}

export function createContainerCollisionDetection(lastOverIdRef) {
   return (args) => {
      let containerOnlyFallback = null;

      const pointerCollisions = pointerWithin(args);
      if (pointerCollisions.length > 0) {
         const specific = classify(pointerCollisions);
         if (specific) {
            lastOverIdRef.current = specific[0].id;
            return [specific[0]];
         }
         containerOnlyFallback = pointerCollisions[0];
      }

      const rectCollisions = rectIntersection(args);
      if (rectCollisions.length > 0) {
         const specific = classify(rectCollisions);
         if (specific) {
            lastOverIdRef.current = specific[0].id;
            return [specific[0]];
         }
         containerOnlyFallback = containerOnlyFallback || rectCollisions[0];
      }

      const closestCollisions = closestCenter(args);
      if (closestCollisions.length > 0) {
         const specific = classify(closestCollisions);
         if (specific) {
            lastOverIdRef.current = specific[0].id;
            return [specific[0]];
         }
         containerOnlyFallback = containerOnlyFallback || closestCollisions[0];
      }

      if (containerOnlyFallback) {
         lastOverIdRef.current = containerOnlyFallback.id;
         return [containerOnlyFallback];
      }
      if (lastOverIdRef.current != null) {
         return [{ id: lastOverIdRef.current }];
      }
      return [];
   };
}
